import { differenceInSeconds, format, isSameDay, addDays, parseISO, subMinutes } from 'date-fns';
import { isAfter, addMinutes, addSeconds, fromUnixTime } from 'date-fns/fp';
export { fromUnixTime } from 'date-fns/fp';
import { addListener } from '@reduxjs/toolkit';

/**
 * Converts a string to a hex colour code
 * @param {string} string
 * @returns Base64 string representation
 */ const stringToColor = (string)=>{
    let hash = 0;
    let i;
    for(i = 0; i < string.length; i += 1){
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for(i = 0; i < 3; i += 1){
        const value = hash >> i * 8 & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
};
const getDynamicColorFromBg = (args)=>{
    const recastBlack = '#1e211d';
    const { darkColor = recastBlack, lightColor = '#ffffff', bgColor } = args;
    const getsRgb = (color)=>{
        const toRgb = (color)=>parseInt(color, 16);
        if (toRgb(color) / 255 <= 0.03928) {
            return toRgb(color) / 255 / 12.92;
        }
        return Math.pow((toRgb(color) / 255 + 0.055) / 1.055, 2.4);
    };
    const getLuminance = (hexColor)=>{
        const r = getsRgb(hexColor.slice(1, 3));
        const g = getsRgb(hexColor.slice(3, 5));
        const b = getsRgb(hexColor.slice(5, 7));
        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    };
    const getContrast = (bgColor, compareColor)=>{
        const l1 = getLuminance(bgColor);
        const l2 = getLuminance(compareColor);
        return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    };
    const getTextColor = (bgColor)=>{
        const whiteContrast = getContrast(bgColor, lightColor);
        const blackContrast = getContrast(bgColor, darkColor);
        if (whiteContrast > blackContrast) {
            return lightColor;
        }
        return darkColor;
    };
    return getTextColor(bgColor);
};

const secondsToDays = (seconds)=>seconds / (60 * 60 * 24);

const daysBetween = (dateA, dateB)=>{
    const seconds = differenceInSeconds(dateA, dateB);
    return Math.ceil(secondsToDays(seconds));
};

/**
 * Time between dates to a formatted string e.g. 2s, 5m, ...
 * @param fromDate
 * @param toDate
 * @returns time in short hand
 */ const timeBetween = (fromDate, toDate)=>{
    const from = fromDate.getTime();
    const to = toDate.getTime();
    const differenceInMs = to - from;
    const seconds = Math.floor(differenceInMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const years = Math.floor(weeks / 52);
    if (years) {
        return `${years}y`;
    } else if (weeks) {
        return `${weeks}w`;
    } else if (days) {
        return `${days}d`;
    } else if (hours) {
        return `${hours}h`;
    } else if (minutes) {
        return `${minutes}m`;
    } else {
        return `${seconds}s`;
    }
};

/**
 * Determines if one date is after a number of minutes from the first
 * This is an Curried function at allows for partial application of
 * the minutes and start date
 * @param minutes
 * @param date
 * @param now
 * @returns true if second date is after
 */ const isAfterMinutes$1 = (minutes)=>(date)=>(now)=>isAfter(addMinutes(minutes)(date))(now);

/**
 * Determines if one date is after a number of minutes from the first
 * @param minutes
 * @param date
 * @param now
 * @returns true if second date is after
 */ const isAfterMinutes = (minutes, date, now)=>isAfterMinutes$1(minutes)(date)(now);

/**
 * Determines if one date is after a number of seconds from the first
 * This is an Curried function at allows for partial application of
 * the seconds and start date
 * @param seconds
 * @param date
 * @param now
 * @returns true if second date is after
 */ const isAfterSeconds$1 = (seconds)=>(date)=>(now)=>isAfter(addSeconds(seconds)(date))(now);

/**
 * Determines if one date is after a number of seconds from the first
 * @param seconds
 * @param date
 * @param now
 * @returns true if second date is after
 */ const isAfterSeconds = (seconds, date, now)=>isAfterSeconds$1(seconds)(date)(now);

const dateToRelativeString = (date, now, locale)=>{
    const hour = format(date, 'HH:mm', {
        locale: locale.dateFns
    });
    if (isSameDay(date, now)) {
        return locale.atHour(hour);
    }
    const tomorrow = addDays(now, 1);
    if (isSameDay(date, tomorrow)) {
        return locale.tomorrowAtHour(hour);
    }
    const formattedDate = format(date, 'eee d MMMM', {
        locale: locale.dateFns
    });
    return locale.dateAtHour(formattedDate, hour);
};

const textToDate = (text)=>{
    const date = parseISO(text);
    return subMinutes(date, date.getTimezoneOffset());
};

const MMM_DD_YYYY = 'MMM dd, yyyy';
const formatISODate = (date, fmt = MMM_DD_YYYY)=>format(parseISO(date), fmt);

/**
 * Takes a Base64 string and decodes it to a Uint8Array
 * @param input
 * @returns
 */ const base64DecodeUint8Array = (input)=>{
    const raw = window.atob(input);
    const rawLength = raw.length;
    const array = new Uint8Array(new ArrayBuffer(rawLength));
    for(let i = 0; i < rawLength; i++)array[i] = raw.charCodeAt(i);
    return array;
};
/**
 * Encodes a `Uint8Array` to a Base64 string representation
 * @param input
 * @returns Base64 string representation
 */ const base64EncodeUint8Array = (input)=>{
    const keyStr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let output = '';
    let chr1, chr2, chr3, enc1, enc2, enc3, enc4;
    let i = 0;
    while(i < input.length){
        chr1 = input[i++];
        chr2 = i < input.length ? input[i++] : Number.NaN;
        chr3 = i < input.length ? input[i++] : Number.NaN;
        enc1 = chr1 >> 2;
        enc2 = (chr1 & 3) << 4 | chr2 >> 4;
        enc3 = (chr2 & 15) << 2 | chr3 >> 6;
        enc4 = chr3 & 63;
        if (isNaN(chr2)) {
            enc3 = enc4 = 64;
        } else if (isNaN(chr3)) {
            enc4 = 64;
        }
        output += keyStr.charAt(enc1) + keyStr.charAt(enc2) + keyStr.charAt(enc3) + keyStr.charAt(enc4);
    }
    return output;
};
/**
 * Converts an array with a buffer to a string
 * @param array
 * @returns string representation
 */ const arrayToString = (array)=>{
    const uint16array = new Uint16Array(array.buffer);
    return String.fromCharCode.apply(null, uint16array);
};

/**
 * Decodes a token of `<T>` from a string
 * On error returns null and logs error if logger provided
 * @param token
 * @param logger
 * @returns
 */ const decodeJwtToken = (token, logger)=>{
    if (token) {
        try {
            const payloadJSON = atob(token.split('.')[1]);
            return JSON.parse(payloadJSON);
        } catch (error) {
            logger?.logError(error);
            return null;
        }
    }
    return null;
};

/**
 * checks for token expiration, but within a tolerance range due to drift
 * @param token decoded token to check
 * @param tolerance in seconds, defaults to 300 seconds (5 minutes)
 * @returns true if potentially expired
 */ const isTokenPotentiallyExpired = (token, date, tolerance = 300)=>isAfterSeconds(-tolerance, fromUnixTime(token.exp), date);

/**
 * checks for refresh token expiration, but within a tolerance range due to drift
 * @param token decoded token to check
 * @param tolerance in seconds, defaults to 7776000 (90 days)
 * @returns true if expired
 */ const isRefreshTokenExpired = (token, date, tolerance = 7776000)=>!isAfterSeconds(tolerance, fromUnixTime(token.iat), date);

const percentageFrom = (dividend, devisor, decimalPlaces)=>{
    if (dividend > devisor) {
        return 100;
    }
    const value = Math.abs(Math.floor(dividend)) / Math.abs(Math.floor(devisor)) * 100;
    return decimalPlaces ? Number(value.toFixed(decimalPlaces)) : Math.round(value);
};

var DisplayPosition;
(function(DisplayPosition) {
    DisplayPosition["LEFT"] = "LEFT";
    DisplayPosition["RIGHT"] = "RIGHT";
})(DisplayPosition || (DisplayPosition = {}));
const castsToCurrency = (casts, paymentConfig)=>casts * paymentConfig.rate / 100_000_000;
const getCurrencyString = (currency, paymentConfig)=>{
    const { paymentCurrencySymbol, paymentCurrencySymbolDisplayPosition, paymentCurrency } = paymentConfig;
    const fixedCurrency = currency.toFixed(2);
    const suffix = paymentCurrencySymbol === '$' ? ` ${paymentCurrency}` : '';
    switch(paymentCurrencySymbolDisplayPosition){
        case "LEFT":
            return `${paymentCurrencySymbol}${fixedCurrency}${suffix}`;
        case "RIGHT":
            return `${fixedCurrency}${paymentCurrencySymbol}${suffix}`;
    }
};
const getCastsBonus = (casts, paymentConfig)=>{
    const { packages } = paymentConfig;
    const packag = [
        ...packages
    ].sort((a, b)=>b.units - a.units).find(({ units })=>casts >= units);
    return packag?.bonus ?? 0;
};
const getRequiredAmountOfCastsWithoutBonus = (castsWithBonus, paymentConfig)=>{
    const { packages } = paymentConfig;
    const possibleResults = packages.filter(({ units })=>castsWithBonus >= units).map(({ units, bonus })=>Math.max(units, castsWithBonus - bonus));
    return Math.min(castsWithBonus, ...possibleResults);
};
const castsToCurrencyString = (casts, paymentConfig)=>{
    const currency = castsToCurrency(casts, paymentConfig);
    return getCurrencyString(currency, paymentConfig);
};

const getPopUpParentUrl = ()=>window.opener && document.referrer;

const externalFlow = ({ start, end, after })=>(dispatch)=>{
        dispatch(start);
        const predicate = Array.isArray(end) ? (action)=>end.some((e)=>e.match(action)) : end.match;
        dispatch(addListener({
            predicate,
            effect: (action, { unsubscribe })=>{
                unsubscribe();
                after(action);
            }
        }));
    };

const createReduxStorage = (entries)=>({
        middleware: ({ getState })=>{
            return (next)=>(action)=>{
                    const previousState = getState();
                    const dispatchResult = next(action);
                    const currentState = getState();
                    if (currentState !== previousState) {
                        entries.forEach(({ selector, entry })=>{
                            const previousValue = selector(previousState);
                            const currentValue = selector(currentState);
                            if (previousValue !== currentValue) {
                                entry.set(currentValue);
                            }
                        });
                    }
                    return dispatchResult;
                };
        },
        init: ()=>(dispatch)=>{
                entries.forEach(({ actionCreator, entry })=>{
                    entry.addListener((value)=>{
                        dispatch(actionCreator(value));
                    });
                    dispatch(actionCreator(entry.get()));
                });
            }
    });

const storageEntry = (storageFactory, key, init)=>{
    try {
        const storage = storageFactory();
        const parseValue = (value)=>{
            try {
                return value ? JSON.parse(value) : init;
            } catch (e) {
                return init;
            }
        };
        return {
            get: ()=>parseValue(storage.getItem(key)),
            set: (value)=>{
                storage.setItem(key, JSON.stringify(value));
            },
            clear: ()=>{
                storage.removeItem(key);
            },
            addListener: (cb)=>{
                const listener = (e)=>{
                    if (e.storageArea === storage && e.key === key) {
                        cb(parseValue(e.newValue));
                    }
                };
                window.addEventListener('storage', listener);
                return {
                    unsubscribe: ()=>window.removeEventListener('storage', listener)
                };
            }
        };
    } catch (e) {
        return {
            get: ()=>init,
            set: ()=>undefined,
            clear: ()=>undefined,
            addListener: ()=>({
                    unsubscribe: ()=>undefined
                })
        };
    }
};
const localStorageEntry = (key, init)=>storageEntry(()=>localStorage, key, init);
const sessionStorageEntry = (key, init)=>storageEntry(()=>sessionStorage, key, init);
const isStorageEnabled = ()=>{
    try {
        localStorage.setItem('__storage_test', 'testvalue');
        localStorage.removeItem('__storage_test');
        return true;
    } catch (e) {
        return false;
    }
};

const getExtension = (value)=>{
    const filenameArray = value.split('.');
    return filenameArray.length > 1 ? filenameArray[filenameArray.length - 1] : undefined;
};

const stringToOneChar = (string)=>{
    return string.length ? string[0].toUpperCase() : '';
};
const trim = (value, trimLength = 15)=>{
    return value.length <= trimLength ? value : value.substring(0, trimLength);
};
const extractPaddingAsNumber = (padding)=>{
    return parseInt(padding.split('px')[0]);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const instanceOfStripeError = (error)=>{
    return 'code' in error && 'message' in error && 'payment_intent' in error && 'type' in error;
};

/** This function periodically calls a test function until either the test
 * returns true or the timeout is reached.
 * - Failures in the test function will be caught and ignored, so that transient
 *   failures don't cause a problem.
 * - setTimeout is used rather than setInterval so that tests don't pile on top
 *   of each other if they run slowly
 *
 * @return Promise<void> a promised which succeeds if the test succeeds before
 * timeout, or fails otherwise */ const testUntilSuccess = (test, config)=>{
    const start = Date.now();
    const isTimeoutExceeded = ()=>Date.now() - start > msTimeout;
    const { msDelay, msTimeout, initialMsDelay } = config;
    return new Promise((resolve, reject)=>{
        const run = async ()=>{
            const result = await test().catch(()=>false);
            if (result) {
                resolve();
            } else if (isTimeoutExceeded()) {
                reject();
            } else {
                setTimeout(run, msDelay);
            }
        };
        if (initialMsDelay) {
            setTimeout(run, initialMsDelay);
        } else {
            run().catch(reject);
        }
    });
};

const getVideoSize = (width)=>{
    const height = Math.round(width * 9 / 16);
    return {
        width,
        height
    };
};

export { DisplayPosition, arrayToString, base64DecodeUint8Array, base64EncodeUint8Array, castsToCurrency, castsToCurrencyString, createReduxStorage, dateToRelativeString, daysBetween, decodeJwtToken, externalFlow, extractPaddingAsNumber, formatISODate, getCastsBonus, getCurrencyString, getDynamicColorFromBg, getExtension, getPopUpParentUrl, getRequiredAmountOfCastsWithoutBonus, getVideoSize, instanceOfStripeError, isAfterMinutes, isAfterSeconds, isRefreshTokenExpired, isStorageEnabled, isTokenPotentiallyExpired, localStorageEntry, percentageFrom, secondsToDays, sessionStorageEntry, storageEntry, stringToColor, stringToOneChar, testUntilSuccess, textToDate, timeBetween, trim };
